import type { HabitCategory, ObjectiveView } from '@contracts';
import { habitCategoryMeta } from '@ui/shared/habits/habitCategory';

type LinkedHabit = ObjectiveView['linkedHabits'][number];

/**
 * Desktop "linked habits" card — brings the phone Objective Detail screen's
 * `LinkedHabitsSection` (habit-to-objective linking, phase-3-plan.md HL-3)
 * into the unified desktop form/edit screen so nothing is lost when Objective
 * Detail and Objective Edit converge into one screen at desktop (owner
 * direct correction: "use a similar layout from the screen you just
 * created... it can't feel different ux"). Matches
 * `LinkedObjectivesSection`'s own card anatomy (label/divider/row list)
 * rather than reusing the phone component directly, since that component
 * hardcodes phone-only horizontal margins. Display-only, same as the phone
 * version and `LinkedObjectivesSection` — habit linking itself still only
 * happens from the habit's own editor (no add affordance here).
 */
export function LinkedHabitsSection({
  linkedHabits,
  habitsById,
}: {
  readonly linkedHabits: ObjectiveView['linkedHabits'];
  readonly habitsById: ReadonlyMap<string, { readonly category: HabitCategory; readonly habitKind: 'build' | 'avoid' }>;
}) {
  return (
    <div style={{ marginTop: 'var(--space-5)', paddingTop: 'var(--space-5)', borderTop: '1px solid var(--border-subtle)' }}>
      <span
        style={{
          fontSize: 'var(--type-label-size)',
          fontWeight: 'var(--type-label-weight)',
          letterSpacing: 'var(--type-label-tracking)',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        Linked habits
      </span>

      {linkedHabits.length === 0 ? (
        <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          No habits linked yet. Link one from the habit&apos;s editor to add it here.
        </p>
      ) : (
        <ul style={{ marginTop: 'var(--space-2)', listStyle: 'none', padding: 0, margin: 0 }}>
          {linkedHabits.map((habit) => (
            <LinkedHabitRow key={habit.habitId} habit={habit} meta={habitsById.get(habit.habitId) ?? null} />
          ))}
        </ul>
      )}
    </div>
  );
}

function LinkedHabitRow({
  habit,
  meta,
}: {
  readonly habit: LinkedHabit;
  readonly meta: { readonly category: HabitCategory; readonly habitKind: 'build' | 'avoid' } | null;
}) {
  const habitKind = meta?.habitKind ?? 'build';
  const { planned, actual, met } = habit.currentWeek;

  let adherenceText: string;
  let ariaSuffix: string;
  let showMetTag = false;

  if (habitKind === 'avoid') {
    adherenceText = `${actual} of ${planned} used this week`;
    ariaSuffix = `${actual} of ${planned} used this week`;
  } else if (met) {
    adherenceText = `${actual} / ${planned} this week`;
    ariaSuffix = `${actual} of ${planned} this week, target met`;
    showMetTag = true;
  } else {
    adherenceText = `${actual} / ${planned} this week`;
    ariaSuffix = `${actual} of ${planned} this week`;
  }

  return (
    <li
      aria-label={`${habit.name}, ${ariaSuffix}`}
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-1) 0' }}
    >
      {meta && <CategoryIconTile category={meta.category} />}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 'var(--type-body-size)',
          fontWeight: 500,
          color: 'var(--text-primary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {habit.name}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flex: 'none' }} aria-hidden="true">
        <span
          className="num"
          style={{
            fontSize: 'var(--type-caption-size)',
            color: habitKind === 'build' && met ? 'var(--text-primary)' : 'var(--text-secondary)',
          }}
        >
          {adherenceText}
        </span>
        {showMetTag && (
          <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--hue-primary)' }}>Met</span>
        )}
      </div>
    </li>
  );
}

function CategoryIconTile({ category }: { readonly category: HabitCategory }) {
  const meta = habitCategoryMeta(category);
  const Icon = meta.Icon;
  return (
    <span
      aria-hidden="true"
      style={{
        width: '20px',
        height: '20px',
        borderRadius: 'var(--radius-sm)',
        background: `color-mix(in srgb, var(--accent-${meta.tokenSuffix}-vivid) 16%, var(--surface-raised))`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
      }}
    >
      <Icon size={12} color={`var(--accent-${meta.tokenSuffix})`} aria-hidden="true" />
    </span>
  );
}
